"use client"

import * as React from "react"

import { User, Lock, Globe } from "lucide-react"

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useAuthStore } from "@/stores/auth-store"
import { EnhancedLanguageSwitcher } from "@/components/ui/enhanced-language-switcher"
import { ProfileForm } from "./profile-form"
import { PasswordForm } from "./password-form"

interface ProfileSettingsOverlayProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function ProfileSettingsOverlay({ open, onOpenChange }: ProfileSettingsOverlayProps) {
  const { user, checkAuth } = useAuthStore()

  const userData = React.useMemo(() => {
    if (!user) return null
    return {
      firstName: user.attributes?.given_name || '',
      lastName: user.attributes?.family_name || '',
      email: user.attributes?.email || '',
      phoneNumber: user.attributes?.phone_number || '',
    }
  }, [user])

  const handleProfileUpdate = React.useCallback(async () => {
    // Refresh user data to get the latest attributes
    await checkAuth()
  }, [checkAuth])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="w-full max-w-2xl mx-auto h-[600px] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-blue-600" />
            Profile and Settings
          </DialogTitle>
          <DialogDescription>
            Manage your profile information, password, and preferences.
          </DialogDescription>
        </DialogHeader>
        
        <Tabs defaultValue="profile" className="flex-1 flex flex-col min-h-0">
          <TabsList className="grid w-full grid-cols-3 flex-shrink-0">
            <TabsTrigger value="profile" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="password" className="flex items-center gap-2">
              <Lock className="h-4 w-4" />
              Password
            </TabsTrigger>
            <TabsTrigger value="preferences" className="flex items-center gap-2">
              <Globe className="h-4 w-4" />
              Preferences
            </TabsTrigger>
          </TabsList>
          
          <div className="flex-1 overflow-y-auto mt-6 min-h-0">
            <TabsContent value="profile" className="h-full">
              <ProfileForm 
                initialData={userData}
                onSuccess={async () => {
                  await handleProfileUpdate()
                  onOpenChange(false)
                }}
              />
            </TabsContent>
            
            <TabsContent value="password" className="h-full">
              <PasswordForm 
                onSuccess={async () => {
                  await handleProfileUpdate()
                  onOpenChange(false)
                }}
              />
            </TabsContent>
             
            <TabsContent value="preferences" className="h-full">
              <div className="space-y-6">
                                 <div>
                   <h3 className="text-lg font-medium mb-4">Preferences</h3>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-600">Language:</span>
                    <EnhancedLanguageSwitcher />
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    Choose your preferred language for the interface.
                  </p>
                </div>
              </div>
            </TabsContent>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}
